import axios from 'axios';
import {getAuthToken} from './utils/localStorageService';
import {logout} from './store/auth';
import {API_BASE} from './config/constants';

/**
 * Creates an Axios instance for making API requests
 *
 * @param {string} token - The authentication token to be used in the request headers
 * @return {AxiosInstance} - The Axios instance configured with the base URL and headers
 */
export const apiClientV1 = (token) =>
    axios.create({
        baseURL: API_BASE,
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

/**
 * Fetches products from the API based on filter, range, and sort parameters
 *
 * @param {Object} filter - The filter criteria to apply to the products
 * @param {Array} range - The range of products to fetch (e.g., [0, 10])
 * @param {Array} sort - The sorting criteria to apply to the products (e.g., ["id", "DESC"])
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the fetched products or undefined if an error occurs
 */
async function products(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const {data} = await apiClientV1(getAuthToken()).get(`/products?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching products:', error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Fetches product data from the API by ID
 *
 * @param {string|number} id - The ID of the product to fetch
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the fetched product data or undefined if an error occurs
 */
async function productById(id) {
    try {
        const {data} = await apiClientV1(getAuthToken()).get(`/products/${id}`);
        return data;
    } catch (error) {
        console.error(`Error fetching product with ID ${id}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Fetches products to copy to from the API
 *
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the fetched products or undefined if an error occurs
 */
async function productsToCopyTo(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });
        const {data} = await apiClientV1(getAuthToken()).get(
            `/products/productsToCopyTo?${params}`,
        );
        return data;
    } catch (error) {
        console.error(`Error fetching product with ID ${id}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Fetches the title of a clearance memo by its mapped product ID from the API
 *
 * @param {string} productId - The ID of the mapped product to fetch the title for
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the fetched title data or undefined if an error occurs
 */
async function titleByMappedProductId(productId) {
    try {
        const {data} = await apiClientV1(getAuthToken()).get(
            `/clearanceMemo/titleByMappedProductId/${productId}`,
        );
        return data;
    } catch (error) {
        console.error(`Error fetching title for product ID ${productId}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Creates a new clearance memo for a product in the API
 *
 * @param {string} productId - The ID of the product to create a clearance memo for
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the created clearance memo or undefined if an error occurs
 */
async function createClearanceMemo(productId) {
    try {
        const {data} = await apiClientV1(getAuthToken()).post(`/clearanceMemo/create`, {
            productId,
        });
        return data;
    } catch (error) {
        console.error(`Error creating clearance memo for product ID ${productId}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Copies a clearance memo to another product in the API
 *
 * @param {string} id - The ID of the clearance memo to copy
 * @param {Object} copyToId - The ID of the product to copy the clearance memo to
 * @param {string} copyToId.value - The value of the product ID to copy to
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the copied clearance memo or undefined if an error occurs
 */
async function copyClearanceMemo(id, copyToId) {
    try {
        const payload = {ids: [copyToId?.value]};
        return await apiClientV1(getAuthToken()).put(
            `/clearanceMemo/copyClearanceMemo/${id}`,
            payload,
        );
    } catch (error) {
        console.error(`Error copying clearance memo with ID ${id}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Deletes a clearance memo by its product ID from the API
 *
 * @param {string} productId - The ID of the product to delete the clearance memo for
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with a deleted clearance memo or undefined if an error occurs
 */
async function deleteClearanceMemo(productId) {
    try {
        return await apiClientV1(getAuthToken()).delete(`/clearanceMemo/delete/${productId}`);
    } catch (error) {
        console.error(`Error deleting clearance memo for product ID ${productId}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Fetches the table of contents (TOC) for a clearance memo by its ID from the API
 *
 * @param {string} id - The ID of the clearance memo to fetch the TOC for
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the fetch TOC or undefined if an error occurs
 */
async function clearanceMemoTOC(id) {
    try {
        const params = new URLSearchParams({filter: JSON.stringify({id})});

        const {data} = await apiClientV1(getAuthToken()).get(
            `/clearanceMemo/getClearanceMemoTOC?${params}`,
        );

        return data;
    } catch (error) {
        console.error(`Error fetching TOC for clearance memo ID ${id}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Updates the table of contents (TOC) for a clearance memo in the API
 *
 * @param {string} id - The ID of the clearance memo to update the TOC for
 * @param {Array} toc - The new TOC data to set for the clearance memo
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the updated TOC or undefined if an error occurs
 */
async function updateClearanceMemoTOC(id, toc) {
    try {
        const {data} = await apiClientV1(getAuthToken()).put(
            `/clearanceMemo/updateClearanceMemoTOC/${id}`,
            toc,
        );

        return data?.map((item) => ({
            title: `${item.sequence} ${item?.clearanceMemoSection?.title}`,
        }));
    } catch (error) {
        console.error(`Error updating TOC for clearance memo ID ${id}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Fetches clearance memo titles from the API based on filter, range, and sort parameters
 *
 * @param {Object} filter - The filter criteria to apply to the clearance memo titles
 * @param {Array} range - The range of clearance memo titles to fetch (e.g., [0, 10])
 * @param {Array} sort - The sorting criteria to apply to the clearance memo titles (e.g., ["id", "DESC"])
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with fetched clearance memo titles or undefined if an error occurs
 */
async function clearanceMemoTitles(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const {data} = await apiClientV1(getAuthToken()).get(`/clearanceMemo/titles?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching clearance memo titles:', error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Creates a new clearance memo title in the API
 *
 * @param {Object} clearanceMemoTitle - The clearance memo title data to create
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with created clearance memo title or undefined if an error occurs
 */
async function createClearanceMemoTitle(clearanceMemoTitle) {
    try {
        const {data} = await apiClientV1(getAuthToken()).post(
            `/clearanceMemo/titles`,
            clearanceMemoTitle,
        );
        return data;
    } catch (error) {
        console.error('Error creating clearance memo title:', error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Fetches shared sections from the API based on the provided filter, range, and sort parameters
 *
 * @param {Object} filter - The filter criteria to apply to the shared sections
 * @param {Array} range - The range of shared sections to fetch (e.g., [0, 10])
 * @param {Array} sort - The sorting criteria to apply to the shared sections (e.g., ["id", "DESC"])
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with fetched shared sections or undefined if an error occurs
 */
async function sharedSections(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const {data} = await apiClientV1(getAuthToken()).get(
            `/clearanceMemo/sharedSections?${params}`,
        );
        return data;
    } catch (error) {
        console.error('Error fetching shared sections:', error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Fetches information on a specific shared section
 *
 * @param {Integer} id - The ID of the shared section
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with fetched shared section info or undefined if an error occurs
 */
async function sharedSectionById(id) {
    try {
        const {data} = await apiClientV1(getAuthToken()).get(
            `/clearanceMemo/sharedSection/${id}`,
        );
        return data;
    } catch (error) {
        console.error('Error fetching shared section:', error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Creates a new section in a clearance memo in the API
 *
 * @param {Object} title - The title data to associate with the section
 * @param {string} title.id - The ID of the title to associate with the section
 * @param {string} title.mappedProductId - The ID of the mapped product to associate with the section
 * @param {boolean} isShared - Whether the section is shared or not
 * @param {Object} selectedNode - The selected node data to associate with the section
 * @param {string} selectedNode.id - The ID of the selected node to associate with the section
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the created section or undefined if an error occurs
 */
async function createSection(title, isShared, selectedNode) {
    try {
        const payload = {
            clearanceMemoTitleId: title?.id,
            parentTOCId: selectedNode?.id ?? 0,
            productId: title?.mappedProductId,
            shared: isShared,
            sharedSectionId: '',
        };

        return await apiClientV1(getAuthToken()).post(
            `/clearanceMemo/createClearanceMemoSection`,
            payload,
        );
    } catch (error) {
        console.error(`Error creating section for clearance memo title ID ${title?.id}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Adds a shared section to a clearance memo in the API
 *
 * @param {Object} section - The section data to add
 * @param {Object} title - The title data to associate with the section
 * @param {string} title.id - The ID of the title to associate with the section
 * @param {string} title.mappedProductId - The ID of the mapped product to associate with the section
 * @param {Object} selectedNode - The selected node data to associate with the section
 * @param {string} selectedNode.id - The ID of the selected node to associate with the section
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the added shared section or undefined if an error occurs
 */
async function addSharedSection(section, title, selectedNode) {
    try {
        const payload = {
            clearanceMemoTitleId: title?.id,
            productId: title?.mappedProductId,
            shared: true,
            sharedSectionId: section?.value,
            parentTOCId: selectedNode?.id ?? 0,
        };

        return await apiClientV1(getAuthToken()).post(
            `/clearanceMemo/createClearanceMemoSection`,
            payload,
        );
    } catch (error) {
        console.error(
            `Error adding shared section for clearance memo title ID ${title?.id}:`,
            error,
        );

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Verifies a position in a clearance memo in the API
 *
 * @param {string} id - The ID of the position to verify
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the verified position or undefined if an error occurs
 */
async function verifyPosition(id) {
    try {
        return await apiClientV1(getAuthToken()).put(`/clearanceMemo/verifyTOCPosition/${id}`);
    } catch (error) {
        console.error(`Error verifying position ID ${id}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Updates a clearance memo section in the API
 *
 * @param {Object} section - The section data to update
 * @param {string} section.id - The ID of the section to update
 * @param {string} section.body - The body of the section to update
 * @param {string} section.clearanceMemoSectionTypeId - The ID of the section type to update
 * @param {boolean} section.isPublic - Whether the section is public or not
 * @param {number} section.status - The status of the section to update
 * @param {string} section.title - The title of the section to update
 * @param {boolean} section.isTitleHidden - Whether the title is hidden or not
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the updated clearance memo section or undefined if an error occurs
 */
async function updateClearanceMemoSection(section) {
    try {
        const payload = {
            body: section?.body,
            clearanceMemoSectionTypeId: section?.clearanceMemoSectionTypeId || '',
            public: section?.isPublic,
            status: section?.status ?? 0,
            title: section?.title,
            titleHidden: section?.isTitleHidden,
        };

        const {data} = await apiClientV1(getAuthToken()).put(
            `/clearanceMemo/updateClearanceMemoTOCAndSection/${section?.id}`,
            payload,
        );

        return data?.map((item) => ({
            title: `${item.sequence} ${item?.clearanceMemoSection?.title}`,
        }));
    } catch (error) {
        console.error(`Error updating clearance memo section ID ${section?.id}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Updates a clearance memo section in the API
 *
 * @param {Object} section - The section data to update
 * @param {string} section.id - The ID of the section to update
 * @param {string} section.body - The body of the section to update
 * @param {string} section.clearanceMemoSectionTypeId - The ID of the section type to update
 * @param {boolean} section.isPublic - Whether the section is public or not
 * @param {number} section.status - The status of the section to update
 * @param {string} section.title - The title of the section to update
 * @param {boolean} section.isTitleHidden - Whether the title is hidden or not
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the updated clearance memo section or undefined if an error occurs
 */
async function updateSharedSection(section) {
    try {
        const payload = {
            body: section?.body,
            clearanceMemoSectionTypeId: section?.clearanceMemoSectionTypeId || '',
            divisionId: section?.divisionId || '',
            productsToAdd: section?.productsToAdd || [],
            public: section?.isPublic || false,
            shareable: section?.isShareable || true,
            status: section?.status || '',
            tagIdList: section?.tagIdList || [],
            title: section?.title,
            unLinkList: section?.unLinkList || [],
        };

        const {data} = await apiClientV1(getAuthToken()).put(
            `/clearanceMemo/updateClearanceMemoSection/${section?.id || 0}`,
            payload,
        );

        return data;
    } catch (error) {
        console.error(`Error updating clearance memo section ID ${section?.id}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Deletes a section by its ID from the API
 *
 * @param {string} id - The ID of the section to delete
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the deleted section or undefined if an error occurs
 */
async function deleteSection(id) {
    try {
        const {data} = await apiClientV1(getAuthToken()).delete(`/clearanceMemo/tocItem/${id}`);
        return data;
    } catch (error) {
        console.error(`Error deleting section ID ${id}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Converts a clearance memo section to a shared section in the API
 *
 * @param {string} id - The ID of the section to convert
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the converted section or undefined if an error occurs
 */
async function convertToSharedSection(id) {
    try {
        return await apiClientV1(getAuthToken()).put(`/clearanceMemo/convertToSharedSection/${id}`);
    } catch (error) {
        console.error(`Error converting section ID ${id} to shared section:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Converts a clearance memo section to a local section in the API
 *
 * @param {string} id - The ID of the section to convert
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the converted section or undefined if an error occurs
 */
async function convertToLocalSection(id) {
    try {
        return await apiClientV1(getAuthToken()).put(`/clearanceMemo/convertToLocalSection/${id}`);
    } catch (error) {
        console.error(`Error converting section ID ${id} to local section:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Unlinks a clearance memo section from its parent section in the API
 *
 * @param {string} id - The ID of the section to unlink
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the unlinked section or undefined if an error occurs
 */
async function unlinkClearanceMemoSection(id) {
    try {
        return await apiClientV1(getAuthToken()).delete(
            `/clearanceMemo/unlinkClearanceMemoSection/${id}`,
        );
    } catch (error) {
        console.error(`Error unlinking clearance memo section ID ${id}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

async function linkedProducts(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const {data} = await apiClientV1(getAuthToken()).get(`/products?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching products:', error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Fetches tags from the API based on the provided filter, range, and sort parameters
 *
 * @param {Object} filter - The filter criteria to apply to the tags
 * @param {Array} range - The range of tags to fetch (e.g., [0, 10])
 * @param {Array} sort - The sorting criteria to apply to the tags (e.g., ["id", "DESC"])
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the fetched tags or undefined if an error occurs
 */
async function tags(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const {data} = await apiClientV1(getAuthToken()).get(`/clearanceMemo/tags?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching tags:', error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Fetches info on clearance memo tags from API
 *
 * @param {Array} tagIds - The list of tagIds to retrieve full info for
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the fetched tags or undefined if an error occurs
 */
async function clearanceMemoTags(tagIds) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify({id: tagIds}),
        });

        const {data} = await apiClientV1(getAuthToken()).get(`/clearanceMemo/tags?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching tags:', error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Creates a new tag in the API
 *
 * @param {string} tag - The name of the tag to create
 * @return {Promise<Object|undefined>} - A promise that resolves with the created tag or undefined if an error occurs
 */
async function createTag(tag) {
    try {
        const {data} = await apiClientV1(getAuthToken()).post(`/clearanceMemo/tags`, tag);
        return data;
    } catch (error) {
        console.error(`Error creating tag: "${tag}"`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Updates an existing tag in the API
 *
 * @param {Object} tag - The tag data to update
 * @param {string} tag.id - The ID of the tag to update
 * @param {string} tag.tagName - The new name of the tag
 * @return {Promise<Object|undefined>} - A promise that resolves with the updated tag or undefined if an error occurs
 */
async function updateTag(tag) {
    try {
        const payload = {tagName: tag?.tagName};

        const {data} = await apiClientV1(getAuthToken()).put(
            `/clearanceMemo/tags/${tag?.id}`,
            payload,
        );

        return data;
    } catch (error) {
        console.error(`Error updating tag ID ${tag?.id}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Fetches contacts from the API based on the provided filter, range, and sort parameters
 *
 * @param {Object} filter - The filter criteria to apply to the contacts
 * @param {Array} range - The range of contacts to fetch (e.g., [0, 10])
 * @param {Array} sort - The sorting criteria to apply to the contacts (e.g., ["id", "DESC"])
 * @return {Promise<Object|undefined>} - A promise that resolves with the fetched contacts or undefined if an error occurs
 */
async function contacts(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const {data} = await apiClientV1(getAuthToken()).get(`/contact?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching contacts:', error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Creates a new contact in the API
 *
 * @param {Object} info - The new contact information to be saved
 * @return {Promise<Object|undefined>} - A promise that resolves with the saved contact info or undefined if an error occurs
 */
async function createContact(info) {
    try {
        const {data} = await apiClientV1(getAuthToken()).post(`/contact`, info);
        return data;
    } catch (error) {
        console.error('Error creating contact:', error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Updates existing contact information for a given contact ID
 *
 * @param {Object} info - The updated contact information to be saved
 * @param {string} info.id - The ID of the contact to update
 * @return {Promise<Object|undefined>} - A promise that resolves with the updated contact info or undefined if an error occurs
 */
async function updateContact(info) {
    try {
        const {data} = await apiClientV1(getAuthToken()).put(`/contact/${info?.id}`, info);
        return data;
    } catch (error) {
        console.error(`Error updating contact ID ${info?.id}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Updates the legal status of a product in the API
 *
 * @param {string} id - The ID of the product to update
 * @param {Object} legalStatus - The new legal status to set for the product
 * @param {string} legalStatus.value - The value of the new legal status
 * @return {Promise<Object|undefined>} - A promise that resolves with the updated product data or undefined if an error occurs
 */
async function updateLegalStatus(id, legalStatus) {
    try {
        const payload = {legalStatusId: legalStatus?.value};
        return await apiClientV1(getAuthToken()).put(`/products/updateLegalStatus/${id}`, payload);
    } catch (error) {
        console.error(`Error updating legal status for product ID ${id}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Fetches contractual party types from the API
 *
 * @return {Promise<Object|undefined>} - A promise that resolves with the fetched contractual party types or undefined if an error occurs
 */
async function contractualPartyTypes() {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify({}),
            range: '[0,50]',
            sort: '["id", "DESC"]',
        });

        const {data} = await apiClientV1(getAuthToken()).get(
            `/clearanceMemo/contractualPartyTypes?${params}`,
        );
        return data;
    } catch (error) {
        console.error('Error fetching contractual party types:', error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Adds contractual parties to a clearance memo in the API
 *
 * @param {string} id - The ID of the clearance memo to update
 * @param {Array} contactIds - An array of contact IDs to add as contractual parties
 * @return {Promise<Object|undefined>} - A promise that resolves with the updated contractual parties or undefined if an error occurs
 */
async function addContractualParties(id, contactIds) {
    try {
        const idsToSave = contactIds?.map(({value}) => value) ?? [];

        const payload = {
            ids: idsToSave,
            clearanceMemoContractualParties: [],
        };

        return await apiClientV1(getAuthToken()).put(
            `/clearanceMemo/saveContractualParties/${id}`,
            payload,
        );
    } catch (error) {
        console.error(`Error adding contractual parties for clearance memo ID ${id}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Updates the contractual parties of a clearance memo in the API
 *
 * @param {string} id - The ID of the clearance memo to update
 * @param {Object} contractualParties - The new contractual parties to set for the clearance memo
 * @param {Array} contractualParties.clearanceMemoContractualParties - An array of contractual parties to set
 * @return {Promise<Object|undefined>} - A promise that resolves with the updated contractual parties or undefined if an error occurs
 */
async function updateContractualParties(id, contractualParties) {
    try {
        const payload = {
            ids: [],
            clearanceMemoContractualParties:
                contractualParties?.clearanceMemoContractualParties ?? [],
        };

        return await apiClientV1(getAuthToken()).put(
            `/clearanceMemo/saveContractualParties/${id}`,
            payload,
        );
    } catch (error) {
        console.error(`Error updating contractual parties for clearance memo ID ${id}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Deletes a contractual party from a clearance memo in the API
 *
 * @param {string} id - The ID of the contractual party to delete
 * @return {Promise<Object|undefined>} - A promise that resolves with the updated contractual parties or undefined if an error occurs
 */
async function deleteContractualParty(id) {
    try {
        return await apiClientV1(getAuthToken()).delete(
            `/clearanceMemo/deleteContractualParty/${id}`,
        );
    } catch (error) {
        console.error(`Error deleting contractual party ID ${id}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Fetches the versions of a clearance memo from the API based on the provided ID
 *
 * @param {string} id - The ID of the clearance memo to fetch versions for
 * @return {Promise<Object|undefined>} - A promise that resolves with the fetched versions or undefined if an error occurs
 */
async function versions(id) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify({id}),
        });

        const {data} = await apiClientV1(getAuthToken()).get(`/clearanceMemo/versions?${params}`);
        return data;
    } catch (error) {
        console.error(`Error fetching versions for clearance memo ID ${id}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Saves a new version of a clearance memo in the API
 *
 * @param {Object} version - The version data to save
 * @param {string} version.clearanceMemoId - The ID of the clearance memo to save the version for
 * @param {string} version.version - The version number to save
 * @param {string} version.versionDate - The date of the version to save
 * @return {Promise<Object|undefined>} - A promise that resolves with the saved version or undefined if an error occurs
 */
async function saveVersion(version) {
    try {
        return await apiClientV1(getAuthToken()).post(`/clearanceMemo/version`, version);
    } catch (error) {
        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Fetches comments from the API based on the provided filter, range, and sort parameters
 *
 * @param {Object} filter - The filter criteria to apply to the comments
 * @param {Array} range - The range of comments to fetch (e.g., [0, 10])
 * @return {Promise<Object|undefined>} - A promise that resolves with the fetched comments or undefined if an error occurs
 */
async function comments(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: sort ? `[${sort}]` : '["commentType", "DESC"]',
        });

        const {data} = await apiClientV1(getAuthToken()).get(
            `/clearanceMemo/productComments?${params}`,
        );
        return data;
    } catch (error) {
        console.error('Error fetching comments:', error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Fetches a comment by its ID from the API
 *
 * @param {string} commentId - The ID of the comment to fetch
 * @return {Promise<Object|undefined>} - A promise that resolves with the fetched comment or undefined if an error occurs
 */
async function commentById(commentId) {
    try {
        const {data} = await apiClientV1(getAuthToken()).get(
            `/clearanceMemo/productComment/${commentId}`,
        );
        return data;
    } catch (error) {
        console.error(`Error fetching comment ID ${commentId}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Saves a comment for a product in the API
 *
 * @param {Object} comment - The comment data to save
 * @param {string} [comment.id] - The ID of the comment to save (if updating an existing comment)
 * @param {string} comment.body - The body of the comment to save
 * @param {string} comment.commentType - The type of the comment to save
 * @param {string} comment.title - The title of the comment to save
 * @param {boolean} [comment.public] - Whether the comment is public or not (default is true)
 * @param {Object} product - The product data to associate the comment with
 * @param {string} product.clearanceMemoTitleId - The ID of the clearance memo title to associate the comment with
 * @param {string} product.foxFinancialTitleId - The ID of the financial title to associate the comment with
 * @param {string} product.productId - The ID of the product to associate the comment with
 * @return {Promise<Object|undefined>} - A promise that resolves with the saved comment or undefined if an error occurs
 */
async function saveComment(comment, product) {
    try {
        const payload = {
            body: comment?.body,
            clearanceMemoTitleId: product?.clearanceMemoTitleId,
            commentType: comment?.commentType,
            foxFinancialTitleId: product?.foxFinancialTitleId,
            productId: product?.productId,
            public: comment?.public ?? true,
            title: comment?.title,
        };

        return await apiClientV1(getAuthToken()).put(
            `/clearanceMemo/productComment/saveProductComment/${comment?.id ?? 0}`,
            payload,
        );
        ;
    } catch (error) {
        console.error(
            `Error saving comment for clearance memo title ID ${product?.clearanceMemoTitleId}:`,
            error,
        );

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Deletes a comment by its ID from the API
 *
 * @param {string} commentId - The ID of the comment to delete
 * @return {Promise<Object|undefined>} - A promise that resolves with the deleted comment or undefined if an error occurs
 */
async function deleteComment(commentId) {
    try {
        const {data} = await apiClientV1(getAuthToken()).delete(
            `/clearanceMemo/productComment/${commentId}`,
        );
        return data;
    } catch (error) {
        console.error(`Error deleting comment ID ${commentId}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

async function exportComments(clearanceMemoTitleId, productId) {
    try {
        const endpoint = clearanceMemoTitleId
            ? `allProductCommentsPDF/${clearanceMemoTitleId}`
            : `allProductCommentsPDFByProductId/${productId}`;

        return await apiClientV1(getAuthToken()).get(
            `clearanceMemo/${endpoint}`,
            {responseType: 'blob'}
        );
    } catch (error) {
        console.error('Error exporting comments PDF:', error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

async function exportComment(clearanceMemoTitleId, productCommentId) {
    try {

        return await apiClientV1(getAuthToken()).get(
            `clearanceMemo/productCommentPDF/${clearanceMemoTitleId}/${productCommentId}`,
            {responseType: 'blob'}
        );
    } catch (error) {
        console.error('Error exporting comment PDF:', error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

async function exportClearanceMemoPDF(foxFinancialTitleId, id, isClearanceMemo, title) {
    try {
        const endpoint =
            `clearanceMemoHTMLToPDF/${foxFinancialTitleId}/${id}/${isClearanceMemo}/${title.replace(/[\W_]+/g," ")}`

        return await apiClientV1(getAuthToken()).get(
            `clearanceMemo/${endpoint}`,
            {responseType: 'blob'}
        );
    } catch (error) {
        console.error('Error exporting comments PDF:', error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Fetches information on a specific clearance memo title
 *
 * @param {Integer} id - The ID of the clearance memo title
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with fetched clearance memo title info or undefined if an error occurs
 */
async function clearanceMemoTitleById(id) {
    try {
        const {data} = await apiClientV1(getAuthToken()).get(
            `/clearanceMemo/title/${id}`,
        );
        return data;
    } catch (error) {
        console.error('Error fetching clearance memo title:', error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Uploads an attachment for a product comment in the API
 *
 * @param {FormData} formData - The form data containing the attachment to upload
 * @return {Promise<Object|undefined>} - A promise that resolves with the uploaded attachment data or undefined if an error occurs
 */
async function uploadAttachment(formData) {
    try {
        const productCommentId = formData.get('productCommentId');

        const {data} = await apiClientV1(getAuthToken()).put(
            `/clearanceMemo/productComment/uploadAttachment/${productCommentId}`,
            formData,
        );

        return data;
    } catch (error) {
        console.error(
            `Error uploading attachment for comment ID ${formData.get('productCommentId')}:`,
            error,
        );

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Uploads a clearance memo attachment in the API
 *
 * @param {FormData} formData - The form data containing the clearance memo attachment to upload
 * @return {Promise<Object|undefined>} - A promise that resolves with the uploaded clearance memo attachment data or undefined if an error occurs
 */
async function uploadClearanceMemoAttachment(formData) {
    console.log("uploadAttachment", formData);
    try {
        const clearanceMemoSectionId = formData.get('clearanceMemoSectionId');

        const {data} = await apiClientV1(getAuthToken()).put(
            `/clearanceMemo/uploadAttachment/${clearanceMemoSectionId}`,
            formData,
        );

        return data;
    } catch (error) {
        console.error(
            `Error uploading clearance memo attachment for section ID ${formData.get(
                'clearanceMemoSectionId',
            )}:`,
            error,
        );

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

async function downloadAttachment(rightsLineFileId, fileName) {
    try {
        return await apiClientV1(getAuthToken()).get(
            `/clearanceMemo/file/${rightsLineFileId}/${fileName}`,
            {responseType: 'blob'}
        );

    } catch (error) {
        console.error(
            `Error downloading clearance memo attachment for rightsLineFileId ${rightsLineFileId}:`,
            error,
        );

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Deletes an attachment by its ID from the API
 *
 * @param {string} attachmentId - The ID of the attachment to delete
 * @param isCommentAttachment - Whether the attachment is associated with a comment or not
 * @return {Promise<Object|undefined>} - A promise that resolves with the deleted attachment or undefined if an error occurs
 */
async function deleteAttachment(attachmentId, isCommentAttachment = false) {
    try {
        const endpoint = isCommentAttachment
            ? `/clearanceMemo/productComment/deleteAttachment/${attachmentId}`
            : `/clearanceMemo/deleteAttachment/${attachmentId}`;
        const {data} = await apiClientV1(getAuthToken()).delete(endpoint);
        return data;
    } catch (error) {
        console.error(`Error deleting attachment ID ${attachmentId}:`, error);
        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }
        return error?.response?.data;
    }
}

/**
 * Fetches a job status by its ID from the API
 *
 * @param {string} jobId - The ID of the job to fetch the status for
 * @return {Promise<Object|undefined>} - A promise that resolves with the job status or undefined if an error occurs
 */
async function jobs(jobId) {
    try {
        return await apiClientV1(getAuthToken()).get(`/jobs/${jobId}`);
    } catch (error) {
        console.error(`Error fetching job status for job ID ${jobId}:`, error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Fetches users from the API based on the provided filter, range, and sort parameters
 *
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the fetched users or undefined if an error occurs
 */
async function users() {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify({roleIds: '8'}),
            range: '[0, 499]',
            sort: '["id", "DESC"]',
        });

        const {data} = await apiClientV1(getAuthToken()).get(`/users?${params}`);

        return data?.map(({username, id}) => ({label: username, value: id})) ?? [];
    } catch (error) {
        console.error('Error fetching users:', error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

/**
 * Handles unauthorized requests by logging out the user and redirecting to the login page
 */
const handleUnauthorizedRequest = () => {
    logout();
    window.location.assign('/login');
};

// Exporting the API functions for use in other parts of the application
export const datastageApi = {
    products,
    productById,
    productsToCopyTo,
    titleByMappedProductId,

    createClearanceMemo,
    copyClearanceMemo,
    deleteClearanceMemo,
    clearanceMemoTOC,
    updateClearanceMemoTOC,
    clearanceMemoTitles,
    createClearanceMemoTitle,
    exportClearanceMemoPDF,
    clearanceMemoTitleById,

    sharedSectionById,
    sharedSections,
    createSection,
    updateSharedSection,
    addSharedSection,
    verifyPosition,
    updateClearanceMemoSection,
    deleteSection,
    convertToSharedSection,
    convertToLocalSection,
    unlinkClearanceMemoSection,
    linkedProducts,

    tags,
    clearanceMemoTags,
    createTag,
    updateTag,

    contacts,
    createContact,
    updateContact,

    updateLegalStatus,
    contractualPartyTypes,
    addContractualParties,
    updateContractualParties,
    deleteContractualParty,

    versions,
    saveVersion,

    comments,
    commentById,
    saveComment,
    deleteComment,
    exportComments,
    exportComment,

    uploadAttachment,
    uploadClearanceMemoAttachment,
    downloadAttachment,
    deleteAttachment,

    jobs,

    users,
};
